import * as React from 'react';
import { useIsMountedRef } from './use-mounted-ref';

export const enum Behavior {
  /** Only clickable while idle, will never leave fulfilled state once reached. Default */
  SingleSuccess,
  /** Only clickable while idle or fulfilled. Will revert back from fulfilled to idle */
  MultipleSuccesses,
  /** Always clickable. Will revert back from fulfilled to idle. */
  KeepEnabled,
}

export const enum Status {
  Default,
  Pending,
  Fulfilled,
  Rejected,
}

export type HookOptions = {
  defaultMinimumPending?: number;
  defaultMaximumFulfilled?: number;
  defaultMaximumRejected?: number;
  behavior?: Behavior;
};

export type PushOptions = {
  minimumPending?: number;
  maximumFulfilled?: number;
  maximumRejected?: number;
};

export type PushFunction = (asyncOperation: Promise<any>, options?: PushOptions) => Promise<void>;

export type State = {
  status: Status;
  disabled: boolean;
};

export function useFeedback({
  behavior = Behavior.MultipleSuccesses,
  defaultMinimumPending = 500,
  defaultMaximumFulfilled = behavior === Behavior.SingleSuccess ? Infinity : 2000,
  defaultMaximumRejected = 2000,
}: HookOptions = {}) {
  const mountedRef = useIsMountedRef();
  const [status, setStatus] = React.useState<Status>(Status.Default);
  const [disabled, setDisabled] = React.useState<boolean>(false);
  const asyncOperationQueueRef = React.useRef<Promise<any>>(Promise.resolve());

  const push = React.useCallback<PushFunction>(
    async (asyncOperation, options = {}) => {
      const {
        minimumPending = defaultMinimumPending,
        maximumFulfilled = defaultMaximumFulfilled,
        maximumRejected = defaultMaximumRejected,
      } = options;
      let queue: Promise<any> = (asyncOperationQueueRef.current = Promise.all([
        asyncOperation,
        asyncOperationQueueRef.current,
      ]));
      const isLastOperation = () => queue === asyncOperationQueueRef.current;

      setStatus(Status.Pending);
      setDisabled(behavior !== Behavior.KeepEnabled);

      // Sometimes you want the spinner to be visible so the user has a chance
      // to notice that something happen. Studies shows that users today expects
      // that things like this take some time. So if it's to quick they
      // assume that something went wrong. I know... stupid... right?
      const minimumPendingTimer = timeout(minimumPending);

      try {
        await Promise.all([queue, minimumPendingTimer]);

        if (isLastOperation()) {
          const nextDisabled = behavior === Behavior.SingleSuccess;

          if (maximumFulfilled > 0 && mountedRef.current) {
            setStatus(Status.Fulfilled);
            setDisabled(nextDisabled);
          }

          if (maximumFulfilled < Infinity) {
            await timeout(maximumFulfilled);

            if (isLastOperation() && mountedRef.current) {
              setStatus(Status.Default);
              setDisabled(nextDisabled);
            }
          }
        }
      } catch {
        if (isLastOperation()) {
          // Enables to push a new async operation again directly
          // when a promise was rejected
          queue = asyncOperationQueueRef.current = Promise.resolve();

          if (maximumRejected > 0 && mountedRef.current) {
            setStatus(Status.Rejected);
            setDisabled(false);
          }

          if (maximumRejected < Infinity) {
            await timeout(maximumRejected);

            if (isLastOperation() && mountedRef.current) {
              setStatus(Status.Default);
              setDisabled(false);
            }
          }
        }
      }
    },
    [behavior, defaultMaximumFulfilled, defaultMaximumRejected, defaultMinimumPending, mountedRef],
  );

  const state: State = React.useMemo(() => ({ status, disabled }), [status, disabled]);

  return [state, push] as const;
}

function timeout(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms));
}
